import React, { RefObject, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';

import { ResponseInfoType } from '../types';
import { FeedbackApi, FeedbackCommentApi } from '../api';
import { ReactComponent as LikeIcon } from '../assets/icons/like.svg';
import { ReactComponent as DislikeIcon } from '../assets/icons/dislike.svg';
import { ReactComponent as FeedbackIcon } from '../assets/icons/feedback.svg';
import { useLanguage } from '../contexts/LanguageContext';
import BookSearchResult from './messages/BookSearchResult';
import { getBookSearchUI } from './messages/getBookSearchUI';

interface ChatWindowProps {
  chatHistory: {
    question: string;
    answer?: string[];
    info?: ResponseInfoType;
    conversationId?: string;
  }[];
  question: string;
  setQuestion: (value: string) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleReset: () => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  stopFetching: () => void;
  isLoading: boolean;
  isStreaming?: boolean;
  inputRef: RefObject<HTMLTextAreaElement>;
  scrollRef: RefObject<HTMLDivElement>;
  autoResize: (isMobile: boolean) => void;
  isRounded: boolean;
  linkify: (text: string) => string;
  showInput?: boolean;
  playTextToSpeech?: (text: string) => void;
  stopTextToSpeech?: () => void;
  isPlayingTTS?: boolean;
  isMobile: boolean;
  toggleListening?: () => void;
  isListening?: boolean;
}

// 마크다운 표를 파싱하는 함수
const parseMarkdownTable = (text: string) => {
  const tablePattern = /(\|[^\n]+\|\n\|[-:\s|]+\|\n(?:\|[^\n]+\|\n?)*)/g;
  const match = tablePattern.exec(text);

  if (!match) return null;

  const tableText = match[0];
  const lines = tableText.trim().split('\n');

  if (lines.length < 3) return null;

  // 헤더 파싱
  const headers = lines[0]
    .split('|')
    .filter(cell => cell.trim())
    .map(cell => cell.trim());

  // 데이터 행 파싱 (구분선 제외하고 2번째 줄부터)
  const rows = lines.slice(2).map(line =>
    line
      .split('|')
      .filter(cell => cell.trim())
      .map(cell => cell.trim())
  );

  return {
    headers,
    rows,
    originalText: tableText,
    beforeTable: text.substring(0, match.index),
    afterTable: text.substring(match.index + match[0].length)
  };
};

const formatTableCellHtml = (text: string, linkify: (text: string) => string) =>
  linkify(
    text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>'),
  );

// MUI 표 컴포넌트
const MUITable = ({
  headers,
  rows,
  linkify,
}: {
  headers: string[];
  rows: string[][];
  linkify: (text: string) => string;
}) => (
  <TableContainer
    component={Paper}
    sx={{
      margin: '16px 0',
      maxWidth: '100%',
      '& .MuiTable-root': {
        fontSize: '14px'
      }
    }}
  >
    <Table size="small" sx={{ minWidth: 300 }}>
      <TableHead>
        <TableRow>
          {headers.map((header, index) => (
            <TableCell
              key={index}
              sx={{
                fontWeight: 'bold',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                padding: '12px 8px',
                fontSize: '14px'
              }}
              dangerouslySetInnerHTML={{
                __html: formatTableCellHtml(header, linkify),
              }}
            />
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, rowIndex) => (
          <TableRow
            key={rowIndex}
            sx={{
              '&:nth-of-type(even)': {
                backgroundColor: '#f8f9fa'
              },
              '&:hover': {
                backgroundColor: '#e9ecef'
              }
            }}
          >
            {row.map((cell, cellIndex) => (
              <TableCell
                key={cellIndex}
                sx={{
                  border: '1px solid #dee2e6',
                  padding: '12px 8px',
                  fontSize: '14px',
                  lineHeight: 1.5,
                  '& a': {
                    color: 'inherit',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: formatTableCellHtml(cell, linkify),
                }}
              />
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

/** 로드킬 신고 페이지 참고링크 — UI에서 숨김 (기능 미사용) */
const isRoadkillRefUrl = (url?: string | null): boolean => {
  if (!url || typeof url !== 'string') return false;
  const s = url.trim().toLowerCase();
  return s.includes('/roadkill');
};

const withoutRoadkillUrls = (urls: string[] | undefined): string[] =>
  (urls || []).filter((u) => typeof u === 'string' && u.trim() !== '' && !isRoadkillRefUrl(u));

const withoutRoadkillRichSources = <T extends { url?: string | null }>(
  richSources: T[] | undefined,
): T[] => (richSources || []).filter((source) => !isRoadkillRefUrl(source?.url));

// Rich Sources 유효성 검사 함수
const hasValidRichSources = (richSources: any[] | undefined) => {
  const filtered = withoutRoadkillRichSources(richSources);
  return filtered.length > 0 &&
         filtered.some(source =>
           source.title?.trim() ||
           source.snippet?.trim() ||
           source.date?.trim() ||
           source.last_updated?.trim() ||
           source.url?.trim() ||  // 이미지 URL (책 표지 등)
           source.author?.trim()  // 저자 정보
         );
};

// 메시지 렌더링 함수
const FACILITY_LIST_SEP = '─────────────────────────';

const renderMessageContent = (msg: string, linkify: (text: string) => string) => {
  const tableData = parseMarkdownTable(msg);

  if (tableData) {
    const { headers, rows, beforeTable, afterTable } = tableData;

    return (
      <div>
        {/* 표 이전 내용 */}
        {beforeTable && (
          <div dangerouslySetInnerHTML={{
            __html: linkify(
              processNonTableText(beforeTable)
            )
          }} />
        )}

        {/* MUI 표 */}
        <MUITable headers={headers} rows={rows} linkify={linkify} />

        {/* 표 이후 내용 */}
        {afterTable && (
          <div dangerouslySetInnerHTML={{
            __html: linkify(
              processNonTableText(afterTable)
            )
          }} />
        )}
      </div>
    );
  }

  // 시설 목록 — 항목별 분리 렌더 (번호 regex·항목 간격)
  if (msg.includes(FACILITY_LIST_SEP)) {
    return renderFacilityListMessage(msg, linkify);
  }

  // 표가 없는 경우 기존 방식
  return (
    <div dangerouslySetInnerHTML={{
      __html: linkify(
        processNonTableText(msg)
      )
    }} />
  );
};

const renderFacilityListMessage = (msg: string, linkify: (text: string) => string) => {
  const firstSep = msg.indexOf(FACILITY_LIST_SEP);
  const secondSep = msg.indexOf(FACILITY_LIST_SEP, firstSep + FACILITY_LIST_SEP.length);

  if (firstSep === -1 || secondSep === -1) {
    return (
      <div
        style={{ whiteSpace: 'pre-wrap' }}
        dangerouslySetInnerHTML={{ __html: linkify(msg) }}
      />
    );
  }

  const before = msg.slice(0, firstSep + FACILITY_LIST_SEP.length);
  const body = msg.slice(firstSep + FACILITY_LIST_SEP.length, secondSep);
  const after = msg.slice(secondSep);
  const items = body.trim().split(/\n(?=\d+\.\s)/).filter((block) => block.trim());

  return (
    <div className="facility-list-message">
      <div dangerouslySetInnerHTML={{ __html: linkify(before) }} />
      {items.map((item, i) => (
        <div
          key={i}
          className="facility-list-item"
          dangerouslySetInnerHTML={{ __html: linkify(item.trim()) }}
        />
      ))}
      <div dangerouslySetInnerHTML={{ __html: linkify(after) }} />
    </div>
  );
};

// 한 줄로 긴 텍스트를 문장 단위로 나눠 가독성 향상
const formatLongParagraph = (text: string): string => {
  const newlineCount = (text.match(/\n/g) || []).length;
  if (newlineCount >= 2 || text.length < 120) return text;

  return text
    .replace(/([다요]\.)\s+(?=[가-힣])/g, '$1\n\n')
    .replace(/(습니다|니다|됩니다|합니다|됩니다)\.\s+(?=[가-힣])/g, '$1.\n\n')
    .replace(/(?<!\d)([.!?。！？])\s+(?=[가-힣A-Z])/g, '$1\n\n');
};

// 표가 아닌 텍스트 처리 (기존 로직)
const processNonTableText = (text: string) => {
  const formatted = formatLongParagraph(text);
  return formatted
    .replace(/^---\s*$/gm, '<hr />')
    .replace(/^###### (.+)$/gm, '<h6>$1</h6>')
    .replace(/^##### (.+)$/gm, '<h5>$1</h5>')
    .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // h1 뒤 빈 줄 정확히 1칸(\n\n). margin과 겹치지 않도록 h1 margin은 0
    .replace(/(<\/h1>)\n*/g, '$1\n')
    // hr 앞뒤 과한 줄바꿈만 정리 (줄 시작 유지 — # 제목 변환 후 적용)
    .replace(/\n{2,}\s*<hr\s*\/?>\s*\n{2,}/gi, '\n<hr />\n')
    // h2~h6 뒤 줄바꿈만 제거
    .replace(/(<\/h[2-6]>)\n+/g, '$1')
    // # 없이 첫 줄(짧은 제목) 뒤 빈 줄 1칸으로 통일
    .replace(/^([^\n#<]{1,50})\n+/, '$1\n\n')
    // 연속 3줄 이상 줄바꿈 → 1줄 (paragraph-break 과다 방지)
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n\s*\n\s*\n/g, '<div class="paragraph-break"></div>')
    // 줄 시작 번호 목록만 처리 (6.29. 같은 날짜는 제외)
    .replace(
      /(^|\n)(?<!\d\.)(\d+)\.(?!\d{1,2}\.)\s+([^\n]+)/g,
      '$1<div class="list-item"><span class="list-number">$2.</span> $3</div>'
    )
    .replace(/(\d{2,4}-\d{2,4}-\d{4})\.\s*([^\n]+)/g, '$1. <span class="phone-text">$2</span>')
    // .replace(/(^|\n|<\/div>)(-|\*)\s+([^\n]+)/g, '$1<div class="list-item"><span class="list-dash">$2</span> $3</div>')
    .replace(/(<\/div>)\s*\n\s*(-|\*|\d+\.)/g, '$1<div class="item-separator"></div>$2')
    // .replace(/([^\n])\n([^\n])/g, '$1<br />$2');
};

const ChatWindow: React.FC<ChatWindowProps> = ({
  chatHistory,
  question,
  setQuestion,
  handleSubmit,
  handleReset,
  handleKeyDown,
  stopFetching,
  isLoading,
  isStreaming = false,
  inputRef,
  scrollRef,
  autoResize,
  isRounded,
  linkify,
  showInput,
  isMobile,
  toggleListening,
  isListening,
  playTextToSpeech,
  stopTextToSpeech,
  isPlayingTTS
}) => {
  const { t } = useLanguage();

  const StatusDots = ({ text }: { text: string }) => (
    <LoadingDotsWrapper>
      <LoadingText>{text}</LoadingText>
      <LoadingDots>
        <span></span><span></span><span></span>
      </LoadingDots>
    </LoadingDotsWrapper>
  );

  const [activeFeedbackIndex, setActiveFeedbackIndex] = useState<number | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [voted, setVoted] = useState<{ [key: number]: number }>({});

  const toggleFeedbackInput = (index: number) => {
    if (activeFeedbackIndex === index) {
      setActiveFeedbackIndex(null);
      setFeedbackText('');
    } else {
      setActiveFeedbackIndex(index);
      setFeedbackText('');
    }
  };

  const handleVote = (index: number, vote: number) => {
    const conversationId = chatHistory[index]?.conversationId;
    if (conversationId) {
      FeedbackApi(vote, conversationId);
      setVoted(prev => ({ ...prev, [index]: vote }));
    }
  };

  const submitFeedback = (index: number) => {
    const conversationId = chatHistory[index]?.conversationId;
    if (conversationId && feedbackText.trim()) {
      FeedbackCommentApi(feedbackText, conversationId);
      alert('피드백이 제출되었습니다.');
      setActiveFeedbackIndex(null);
      setFeedbackText('');
    }
  };

  return (
    <>
      <ChatScrollArea>
        <ChatContentWrapper>
          {chatHistory.map((entry, i) => {
            const isLastEntry = i === chatHistory.length - 1;
            const isActiveStream = isLastEntry && isStreaming;

            return (
            <ChatBubble key={i}>
              <UserBubbleWrapper>
                <UserBubble>{entry.question}</UserBubble>
              </UserBubbleWrapper>

              {(() => {
                const bookSearchUI = getBookSearchUI(entry.info);
                const isLibraryBookResult = !!bookSearchUI;

                return (
                  <>
              {entry.answer?.map((msg, j) => {
                const isWaitingForResponse =
                  isActiveStream &&
                  j === (entry.answer?.length || 0) - 1 &&
                  (msg === '__loading__' || !msg?.trim());

                return isWaitingForResponse ? (
                  <AiBubble key={j} $libraryUi={isLibraryBookResult}>
                    <StatusDots text={t.chatWindow.searching} />
                  </AiBubble>
                ) : (
                  <AiBubble key={j} $libraryUi={isLibraryBookResult}>
                    {renderMessageContent(msg, linkify)}
                    {/* 도서 카드 UI: 버튼은 카드·안내 문구 아래에 표시 */}
                    {j === (entry.answer?.length || 0) - 1 && !isActiveStream && !isLibraryBookResult && (
                      <ButtonRow>
                        {playTextToSpeech && (
                          <TTSButton
                            onClick={() => {
                              if (isPlayingTTS && stopTextToSpeech) {
                                stopTextToSpeech();
                              } else if (playTextToSpeech) {
                                playTextToSpeech(msg);
                              }
                            }}
                            $isPlaying={isPlayingTTS}
                          >
                            {isPlayingTTS ? t.chatWindow.stopTTS : t.chatWindow.playTTS}
                          </TTSButton>
                        )}
                      </ButtonRow>
                    )}
                  </AiBubble>
                );
              })}

              {/* Extra Info */}
              {entry.info && (() => {
                const info = entry.info!;
                const displayUrls = withoutRoadkillUrls(info.url);
                const displayRichSources = withoutRoadkillRichSources(info.rich_sources);
                const showExtra =
                  info.contact?.department ||
                  info.contact?.phone ||
                  info.contact?.name ||
                  info.contact?.email ||
                  displayUrls.length > 0 ||
                  (Array.isArray(info.images) && info.images.filter(img => img.trim() !== '').length > 0) ||
                  isLibraryBookResult ||
                  hasValidRichSources(info.rich_sources);

                if (!showExtra) return null;

                return (
                  <>
                    {/* 일반 응답: 구분선 → 담당부서 먼저 / 도서검색: 카드·버튼 후 담당부서 */}
                    {!isLibraryBookResult && <InfoDivider />}

                    {!isLibraryBookResult && info.contact?.department && (
                      <InfoBox>
                        <strong>{t.chatWindow.department}:</strong> {info.contact.department}
                      </InfoBox>
                    )}

                    {!isLibraryBookResult && info.contact?.phone && (
                      <InfoBox
                        dangerouslySetInnerHTML={{ __html: linkify(`${t.chatWindow.phone}: ${info.contact.phone}`) }}
                      />
                    )}

                    {!isLibraryBookResult && info.contact?.name && (
                      <InfoBox>
                        <strong>{t.chatWindow.name}:</strong> {info.contact.name}
                      </InfoBox>
                    )}

                    {!isLibraryBookResult && info.contact?.email && (
                      <InfoBox
                        dangerouslySetInnerHTML={{ __html: linkify(`${t.chatWindow.email}: ${info.contact.email}`) }}
                      />
                    )}

                    {info.permitEntryUrl && (
                      <InfoBox>
                        <a
                          href={info.permitEntryUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-block',
                            background: '#1D4ED8',
                            color: '#fff',
                            padding: '10px 14px',
                            borderRadius: 8,
                            textDecoration: 'none',
                            fontWeight: 700,
                            fontSize: 13,
                          }}
                        >
                          영업허가 입지점검 서비스(장사명당) 페이지 바로가기
                        </a>
                      </InfoBox>
                    )}

                    {/* 도서 검색: 안내 → 카드 → 누리집 안내·버튼 → 구분선 → 담당부서 */}
                    {isLibraryBookResult && bookSearchUI && (() => {
                      const lastMsg =
                        entry.answer?.[entry.answer.length - 1] &&
                        entry.answer[entry.answer.length - 1] !== '__loading__'
                          ? entry.answer[entry.answer.length - 1]
                          : '';
                      const afterBooks =
                        bookSearchUI.after_content ||
                        info.library_after_books ||
                        '';
                      const ttsText = [lastMsg, afterBooks].filter(Boolean).join('\n\n');
                      return (
                        <>
                          <LibraryTypedWrap>
                            <BookSearchResult
                              ui={bookSearchUI}
                              afterContent={afterBooks}
                              renderText={(text) => renderMessageContent(text, linkify)}
                              showActions={!isActiveStream}
                              playTTSLabel={t.chatWindow.playTTS}
                              stopTTSLabel={t.chatWindow.stopTTS}
                              isPlayingTTS={isPlayingTTS}
                              playTextToSpeech={playTextToSpeech}
                              stopTextToSpeech={stopTextToSpeech}
                              ttsText={ttsText}
                            />
                          </LibraryTypedWrap>
                          {!isActiveStream && <InfoDivider />}
                          {info.contact?.department && (
                            <InfoBox>
                              <strong>{t.chatWindow.department}:</strong> {info.contact.department}
                            </InfoBox>
                          )}
                          {info.contact?.phone && (
                            <InfoBox
                              dangerouslySetInnerHTML={{
                                __html: linkify(`${t.chatWindow.phone}: ${info.contact.phone}`),
                              }}
                            />
                          )}
                        </>
                      );
                    })()}

                    {/* Rich Sources (도서 book_cover는 카드 UI로 대체됨) */}
                    {!isLibraryBookResult && hasValidRichSources(info.rich_sources) && (
                      <RichSourcesContainer>
                        <RichSourcesTitle>참고 자료</RichSourcesTitle>
                        <RichSourcesGrid>
                          {displayRichSources.map((source, idx) => (
                            <RichSourceCard key={idx}>
                              <RichSourceHeader>
                                <RichSourceTitle
                                  href={(source.url && String(source.url).trim()) || displayUrls[idx] || '#'}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {source.title || `참고 자료 ${idx + 1}`}
                                </RichSourceTitle>
                                {source.author && (
                                  <BookAuthor>
                                    저자: {source.author}
                                  </BookAuthor>
                                )}
                                {(source.date || source.last_updated) && (
                                  <RichSourceDate>
                                    {source.date && `게시일: ${source.date}`}
                                    {source.date && source.last_updated && ' | '}
                                    {source.last_updated && `수정일: ${source.last_updated}`}
                                  </RichSourceDate>
                                )}
                              </RichSourceHeader>
                              {source.snippet && (
                                <RichSourceSnippet>
                                  {source.snippet}
                                </RichSourceSnippet>
                              )}
                            </RichSourceCard>
                          ))}
                        </RichSourcesGrid>
                      </RichSourcesContainer>
                    )}

                    {/* URL 데이터를 카드 형태로 표시 (rich_sources가 없거나 유효하지 않은 경우) */}
                    {displayUrls.length > 0 &&
                     !hasValidRichSources(info.rich_sources) && (
                      <RichSourcesContainer>
                        <RichSourcesTitle>참고 자료</RichSourcesTitle>
                        <RichSourcesGrid>
                          {displayUrls.map((url, idx) => {
                              // URL에서 도메인명이나 제목 추출
                              const getUrlTitle = (urlString: string) => {
                                try {
                                  const urlObj = new URL(urlString);
                                  const hostname = urlObj.hostname.replace('www.', '');

                                  // 특정 사이트별 제목 매핑
                                  const siteNames: { [key: string]: string } = {
                                    'ko.wikipedia.org': '위키백과',
                                    'kr.boell.org': '하인리히 뵐 재단',
                                    'namu.wiki': '나무위키',
                                    'mohw.go.kr': '보건복지부',
                                    'youtube.com': 'YouTube',
                                    'tistory.com': '티스토리 블로그',
                                  };

                                  // 도메인명에서 사이트명 찾기
                                  for (const [domain, name] of Object.entries(siteNames)) {
                                    if (hostname.includes(domain)) {
                                      return name;
                                    }
                                  }

                                  // 기본적으로 도메인명 사용
                                  return hostname.charAt(0).toUpperCase() + hostname.slice(1);
                                } catch {
                                  return `참고 자료 ${idx + 1}`;
                                }
                              };

                              return (
                                <RichSourceCard key={idx}>
                                  <RichSourceHeader>
                                    <RichSourceTitle
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {getUrlTitle(url)}
                                    </RichSourceTitle>
                                  </RichSourceHeader>
                                  <RichSourceSnippet>
                                    {url}
                                  </RichSourceSnippet>
                                </RichSourceCard>
                              );
                            })}
                        </RichSourcesGrid>
                      </RichSourcesContainer>
                    )}

                    {info.images && info.images.length > 0 && (
                      <ImageBox>
                        {Array.isArray(info.images) && info.images
                          .filter(src => src && src.trim() !== '')
                          .map((src, idx) => (
                            <img key={idx} src={src} alt={`관련 이미지 ${idx + 1}`} />
                          ))}
                      </ImageBox>
                    )}

                    {/* 파일 정보 표시 */}
                    {(info.orignlFileNm || info.fileUrl || info.fileExtsn) && (() => {
                      // orignlFileNm: 파일명에 쉼표가 있을 수 있음(예: "게임제공업소_신고(신규, 변경, 폐업).hwp") → ".[ext]," 패턴으로 분리
                      const parseFileNames = (str: string | undefined): string[] => {
                        if (!str?.trim()) return [];
                        const extPattern = /\.(hwpx|hwp|pdf|docx?|xlsx?|pptx?|png|jpe?g|gif|webp|zip|txt)\s*,\s*/gi;
                        return str.replace(extPattern, (_, ext: string) => `.${ext}|||`).split('|||').map((s: string) => s.trim()).filter(Boolean);
                      };
                      const fileNames = parseFileNames(info.orignlFileNm);
                      const fileUrls = info.fileUrl?.split(',').map((url: string) => url.trim()).filter(Boolean) || [];
                      const fileExtensions = info.fileExtsn?.split(',').map((ext: string) => ext.trim()).filter(Boolean) || [];

                      // fileUrls 개수를 기준으로 (실제 파일 수 반영), 부족한 fileName은 '파일 N' 폴백
                      const maxLength = Math.max(fileNames.length, fileUrls.length, fileExtensions.length);

                      if (maxLength === 0) return null;

                      return (
                        <FileInfoContainer>
                          <FileInfoTitle>첨부 파일 {maxLength > 1 ? `(${maxLength}개)` : ''}</FileInfoTitle>
                          <FileInfoGrid $hasMultiple={maxLength > 1}>
                            {Array.from({ length: maxLength }, (_, index) => {
                              // 이응DB 메타데이터 오류 보정: (신규, 변경, 폐업) 또는 (신규,_변경,_폐업) → 실제 파일명 (신규_변경_폐업)
                              const rawName = fileNames[index] || `파일 ${index + 1}`;
                              const fileName = rawName.replace(/\(신규\s*[,]\s*_?\s*변경\s*[,]\s*_?\s*폐업\)/g, '(신규_변경_폐업)');
                              const fileUrl = fileUrls[index];
                              const fileExt = fileExtensions[index];

                                // 이미지 파일 확장자 체크
                                const isImageFile = Boolean(fileExt && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt.toLowerCase()));

                                return (
                                <FileInfoCard key={index} $isImage={isImageFile}>
                                  <FileInfoHeader>
                                    <FileInfoName>
                                      {fileName}
                                    </FileInfoName>
                                    {fileExt ? (
                                      <FileExtension $isImage={isImageFile}>
                                        {fileExt.toUpperCase()}
                                      </FileExtension>
                                    ) : null}
                                  </FileInfoHeader>

                                  {isImageFile && fileUrl ? (
                                    // 이미지 파일인 경우 이미지로 표시
                                    <FileImageContainer>
                                      <FileImage
                                        src={fileUrl}
                                        alt={fileName}
                                        onError={(e) => {
                                          // 이미지 로드 실패시 다운로드 링크로 대체
                                          const target = e.target as HTMLImageElement;
                                          const container = target.parentElement;
                                          if (container) {
                                            container.innerHTML = `
                                              <a href="${fileUrl}" target="_blank" rel="noopener noreferrer" download="${fileName}"
                                                 style="display: inline-flex; align-items: center; gap: 0.5rem; background: linear-gradient(135deg, #22468A, #4285f4); color: white; padding: 0.8rem 1.5rem; border-radius: 8px; text-decoration: none; font-weight: 600; font-size: 1.3rem; transition: all 0.2s ease;">
                                                📎 파일 다운로드
                                              </a>
                                            `;
                                          }
                                        }}
                                      />
                                      <FileImageOverlay className="file-image-overlay">
                                        <FileDownloadLink
                                          href={fileUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          download={fileName}
                                        >
                                          원본 다운로드
                                        </FileDownloadLink>
                                      </FileImageOverlay>
                                    </FileImageContainer>
                                  ) : fileUrl ? (
                                    // 일반 파일인 경우 다운로드 링크
                                    <FileDownloadLink
                                      href={fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      download={fileName}
                                    >
                                      파일 다운로드
                                    </FileDownloadLink>
                                  ) : null}
                                </FileInfoCard>
                              );
                            })}
                          </FileInfoGrid>
                        </FileInfoContainer>
                      );
                    })()}

                    <FeedbackContainer>
                      <button
                        type="button"
                        onClick={() => handleVote(i, 1)}
                        aria-label={t.chatWindow.likeAnswer}
                        style={{
                          backgroundColor: voted[i] === 1 ? 'rgba(85, 12, 221, 0.1)' : 'transparent'
                        }}
                        disabled={!!voted[i]}
                      >
                        <LikeIcon
                          aria-hidden
                          style={{
                            fill: voted[i] === 1 ? 'currentColor' : 'none',
                            stroke: voted[i] === 1 ? 'currentColor' : 'currentColor',
                            color: voted[i] === 1 ? '#10B981' : '#6B7280'
                          }}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleVote(i, -1)}
                        aria-label={t.chatWindow.dislikeAnswer}
                        style={{
                          backgroundColor: voted[i] === -1 ? 'rgba(239, 68, 68, 0.1)' : 'transparent'
                        }}
                        disabled={!!voted[i]}
                      >
                        <DislikeIcon
                          aria-hidden
                          style={{
                            fill: voted[i] === -1 ? 'currentColor' : 'none',
                            stroke: voted[i] === -1 ? 'currentColor' : 'currentColor',
                            color: voted[i] === -1 ? '#EF4444' : '#6B7280'
                          }}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleFeedbackInput(i)}
                        aria-label={t.chatWindow.openFeedback}
                      >
                        <FeedbackIcon aria-hidden />
                      </button>
                    </FeedbackContainer>

                    <FeedbackInputWrapper isVisible={activeFeedbackIndex === i}>
                      <input
                        type="text"
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder={t.chatWindow.feedbackPlaceholder}
                      />
                      <button onClick={() => submitFeedback(i)}>{t.chatWindow.submit}</button>
                    </FeedbackInputWrapper>

                  </>
                );
              })()}
                  </>
                );
              })()}

            </ChatBubble>
            );
          })}

          <div ref={scrollRef} style={{ height: '1px', visibility: 'hidden' }} />
        </ChatContentWrapper>
      </ChatScrollArea>
    </>
  );
};


const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
  align-items: center;
  justify-content: flex-start;
`;

const LibraryTypedWrap = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-wrap: anywhere;
  word-break: break-word;
  padding: 0;
  margin: 0;
  font-size: 16px;
  line-height: 1.7;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 1.6;
  }
`;

const sharedButtonStyles = `
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  min-height: 2.8rem;
  line-height: 1.4;
`;

const TTSButton = styled.button<{ $isPlaying?: boolean }>`
  ${sharedButtonStyles}
  padding: 0.5rem 1rem;
  background: ${props => props.$isPlaying
    ? 'linear-gradient(135deg, #e74c3c, #c0392b)'
    : 'linear-gradient(135deg,rgb(102, 126, 234),rgb(118, 75, 162))'
  };
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 1.3rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  ${props => props.$isPlaying && css`
    animation: pulse 1.5s ease-in-out infinite alternate;

    @keyframes pulse {
      0% { box-shadow: 0 0 0 0 rgba(231, 76, 60, 0.7); }
      100% { box-shadow: 0 0 0 10px rgba(231, 76, 60, 0); }
    }
  `}

  &:hover {
    background: ${props => props.$isPlaying
      ? 'linear-gradient(135deg, #c0392b, #a93226)'
      : 'linear-gradient(135deg,rgb(102, 126, 234),rgb(118, 75, 162))'
    };
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default ChatWindow;

const ChatScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem 1rem; /* 모바일에서 좌우 패딩 추가 */
  min-height: 0;
  background: transparent; /* 배경을 투명하게 변경 */

  /* 모바일에서 더 넓은 좌우 간격 */
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
  scrollbar-width: thin;
  scrollbar-color: rgba(189, 199, 216, 0.6) transparent;
  scroll-behavior: auto; /* 부드러운 스크롤 비활성화 */

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(189, 199, 216, 0.6);
    border-radius: 10px;
    border: 2px solid transparent;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(149, 165, 198, 0.8);
  }
`;

const ChatContentWrapper = styled.div`
  padding-left: 3rem;
  padding-right: 3rem;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 100%;

  @media (max-width: 768px) {
    padding-left: 1rem;
    padding-right: 1rem;
    max-width: 100%;
  }
`;

const ChatBubble = styled.div`
  margin-bottom: 2.5rem;
  position: relative;
`;

const UserBubble = styled.div`
  background: linear-gradient(135deg, #34495e 0%, #2c3e50 100%);
  color: white;
  padding: 1.2rem 1.8rem;
  border-radius: 25px 25px 5px 25px;
  display: inline-block;
  max-width: 80%; /* 좌우 간격 더 확보 */
  text-align: left;
  word-break: break-word;
  font-size: 16px;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(44, 62, 80, 0.3);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: -8px;
    width: 0;
    height: 0;
    border: 8px solid transparent;
    border-top-color: #2c3e50;
    border-left-color: #2c3e50;
    transform: rotate(-45deg);
  }

  @media (max-width: 768px) {
    max-width: 90%;
    padding: 1rem 1.5rem;
    font-size: 15px;
  }
`;

const AiBubble = styled.div<{ $libraryUi?: boolean }>`
  padding: 1.5rem 2rem;
  font-size: 16px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  font-weight: 400;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  border: none;
  position: relative;

  max-width: 80%; /* 좌우 간격 더 확보 */
  margin-left: 0;
  margin-right: auto;
  margin-bottom: 1.5rem;
  min-width: 0;
  box-sizing: border-box;

  ${({ $libraryUi }) =>
    $libraryUi &&
    css`
      max-width: 100%;
      padding-left: 0;
      padding-right: 0;
      margin-bottom: 0.35rem;
    `}

  a {
    color: ${props => props.theme.textDefaultColor === '#FFF' ? '#60A5FA' : '#22468A'};
    text-decoration: underline;
  }

  .facility-list-item {
    white-space: pre-wrap;
    margin-top: 1em;
  }

  .facility-list-item:first-of-type {
    margin-top: 0;
  }

  strong, b {
    font-weight: bold;
    color: ${props => props.theme.textDefaultColor === '#FFF' ? '#FFF' : '#22468A'};
  }

  h1, h2, h3, h4, h5, h6 {
    color: ${props => props.theme.textDefaultColor === '#FFF' ? '#FFF' : '#22468A'};
    font-weight: 600;
    line-height: 1.3;
  }

  h1 {
    font-size: 1.8rem;
    margin: 0;
  }

  h1 + h2 {
    margin-top: 0;
  }

  h2 {
    font-size: 1.6rem;
    margin: 0.45rem 0 0.1rem 0;
  }

  h3 {
    font-size: 1.4rem;
    margin: 0.35rem 0 0.1rem 0;
  }

  h4 {
    font-size: 1.3rem;
    margin: 0.3rem 0 0.1rem 0;
  }

  h5 {
    font-size: 1.2rem;
    margin: 0.25rem 0 0.1rem 0;
  }

  h6 {
    font-size: 1.1rem;
    margin: 0.2rem 0 0.1rem 0;
  }

  hr {
    border: none;
    border-top: 1px solid ${props => props.theme.borderGrayColor || '#dee2e6'};
    margin: 0.75rem 0 0.35rem 0;
  }

  br {
    display: block;
    margin: 0.2rem 0;
    content: "";
  }

  .list-item {
    display: block;
    margin: 0.5rem 0;
    padding-left: 0.2rem;
    position: relative;
    clear: both;
  }

  .list-number {
    font-weight: 600;
    color: ${props => props.theme.textDefaultColor === '#FFF' ? '#FFF' : '#22468A'};
    margin-right: 0.3rem;
  }

  .list-dash {
    font-weight: 500;
  }

  .phone-text {
    display: inline-block;
    margin-left: 0.3rem;
  }

  .paragraph-break {
    height: 0.8rem;
    clear: both;
  }

  .item-separator {
    height: 0.8rem;
    clear: both;
  }

  /* 마크다운 표 스타일 */
  .markdown-table {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
    border: 1px solid ${props => props.theme.borderGrayColor};
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .markdown-table th {
    background: ${props => props.theme.questionBoxBg};
    color: ${props => props.theme.textDefaultColor === '#FFF' ? '#FFF' : '#22468A'};
    font-weight: 600;
    padding: 1rem 1.5rem;
    text-align: left;
    border-bottom: 2px solid ${props => props.theme.borderGrayColor};
    border-right: 1px solid ${props => props.theme.borderGrayColor};
  }

  .markdown-table th:last-child {
    border-right: none;
  }

  .markdown-table td {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid ${props => props.theme.borderGrayColor};
    border-right: 1px solid ${props => props.theme.borderGrayColor};
    vertical-align: top;
  }

  .markdown-table td:last-child {
    border-right: none;
  }

  .markdown-table tbody tr:last-child td {
    border-bottom: none;
  }

  .markdown-table tbody tr:nth-child(even) {
    background: ${props => props.theme.questionBoxBg};
  }

  .markdown-table tbody tr:hover {
    background: ${props => props.theme.questionBoxBg};
    filter: brightness(1.1);
    transition: all 0.2s ease;
  }

  @media (max-width: 768px) {
    padding: 0.8rem 0;
    font-size: 16px;
    line-height: 1.6;
    max-width: 100%;

    .paragraph-break {
      height: 0.6rem;
    }

    .item-separator {
      height: 0.6rem;
    }

    .list-item {
      margin: 0.4rem 0;
    }

    /* 모바일용 표 스타일 */
    .markdown-table {
      font-size: 14px;
      margin: 0.8rem 0;
    }

    .markdown-table th,
    .markdown-table td {
      padding: 0.8rem 1rem;
    }

    .markdown-table th {
      font-size: 14px;
      font-weight: 600;
    }
  }
`;

const UserBubbleWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-bottom: 1rem;
  padding-right: 0;
`;

const LoadingDotsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 1.4rem;
  color: ${props => props.theme.textDefaultColor};
  font-weight: 500;
  padding: 0.6rem 1.2rem;
  background: ${props => props.theme.bodyBgColor};
  border-radius: 24px;
`;

const LoadingText = styled.span`
  white-space: nowrap;
`;

const LoadingDots = styled.div`
  display: inline-flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;

  span {
    width: 0.7rem;
    height: 0.7rem;
    background: linear-gradient(135deg, ${props => props.theme.logoColor} 0%, ${props => props.theme.btnColor} 100%);
    border-radius: 50%;
    animation: gentlePulse 2s infinite ease-in-out both;
    box-shadow: 0 0 10px rgba(102, 126, 234, 0.4);
  }

  span:nth-child(1) {
    animation-delay: -0.6s;
  }

  span:nth-child(2) {
    animation-delay: -0.3s;
  }

  span:nth-child(3) {
    animation-delay: 0s;
  }

  @keyframes gentlePulse {
    0%, 100% {
      transform: scale(0.8);
      opacity: 0.6;
    }
    50% {
      transform: scale(1.2);
      opacity: 1;
    }
  }
`;

const InfoBox = styled.div`
  margin-top: 0.5rem;
  font-size: 15px;
  color: #555;
  padding: 1rem 1.2rem;
  background-color: #f8f9fa;
  border-radius: 12px;
  border: none;
  font-weight: 500;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  box-sizing: border-box;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  a {
    display: inline-block;
    margin-right: 1rem;
    color: #4285f4;
    text-decoration: underline;
    word-break: break-all;
    overflow-wrap: break-word;
    max-width: 100%;
    line-height: 1.4;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 0.8rem 1rem;
    margin-top: 0.8rem;
    border-radius: 10px;
  }
`;

const ImageBox = styled.div`
  margin-top: 1rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;

  img {
    width: 100%;
    height: auto;
    margin-bottom: 0.5rem;
    border-radius: 10px;
    border: 1px solid #ddd;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.02);
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 0.8rem;
    margin-top: 0.8rem;
  }
`;

const InfoDivider = styled.hr`
  border: none;
  border-top: 2px solid #e8eaed;
  width: 100%;
  margin: 1.5rem 0;
  border-radius: 1px;

  @media (max-width: 768px) {
    margin: 1.2rem 0;
    border-top-width: 1px;
  }
`;

const FeedbackContainer = styled.div`
  margin-top: 1.5rem;
  display: flex;
  gap: 0.8rem;
  padding: 0.5rem;
  border-radius: 8px;

  button {
    background: none;
    border: none;
    padding: 0.5rem;
    border-radius: 50%;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    svg {
      width: 22px;
      height: 22px;
      fill: none;
      pointer-events: none;
    }

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
      transform: translateY(-2px);
    }
  }

  @media (max-width: 768px) {
    margin-top: 1.2rem;
    gap: 0.6rem;

    button {
      padding: 0.4rem;

      svg {
        width: 20px;
        height: 20px;
      }
    }
  }
`;

const FeedbackInputWrapper = styled.div<{ isVisible: boolean }>`
  overflow: hidden;
  max-height: ${({ isVisible }) => (isVisible ? '100px' : '0')};
  opacity: ${({ isVisible }) => (isVisible ? '1' : '0')};
  transform: ${({ isVisible }) => (isVisible ? 'translateY(0)' : 'translateY(-10px)')};
  transition: all 0.3s ease;

  display: flex;
  gap: 0.8rem;
  margin-top: ${({ isVisible }) => (isVisible ? '1rem' : '0')};
  padding: ${({ isVisible }) => (isVisible ? '0.5rem 0' : '0')};

  input {
    flex: 1;
    padding: 0.8rem 1.2rem;
    border: 1px solid ${props => props.theme.borderGrayColor};
    border-radius: 8px;
    font-size: 1.4rem;
    font-weight: 500;
    background: ${props => props.theme.bgColor};
    color: ${props => props.theme.textDefaultColor};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus {
      outline: none;
      border-color: ${props => props.theme.logoColor};
      box-shadow: 0 0 0 2px ${props => props.theme.logoColor}20;
    }
  }

  button {
    padding: 0.8rem 1.5rem;
    background-color: ${props => props.theme.logoColor};
    color: ${props => props.theme.whiteText};
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
      background-color: ${props => props.theme.btnColor};
      transform: translateY(-2px);
    }
  }

  @media (max-width: 768px) {
    gap: 0.6rem;
    margin-top: ${({ isVisible }) => (isVisible ? '0.8rem' : '0')};

    input {
      padding: 0.7rem 1rem;
      font-size: 1.3rem;
    }

    button {
      padding: 0.7rem 1.2rem;
      font-size: 1.3rem;
    }
  }
`;


// Rich Sources 카드 스타일 컴포넌트들
const RichSourcesContainer = styled.div`
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    margin-top: 1.2rem;
  }
`;

const RichSourcesTitle = styled.h4`
  font-size: 1.6rem;
  font-weight: 600;
  color: ${props => props.theme.textDefaultColor === '#FFF' ? '#FFF' : '#22468A'};
  margin-bottom: 1rem;
  margin-top: 0;

  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 0.8rem;
  }
`;

const RichSourcesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.8rem;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1025px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const RichSourceCard = styled.div`
  background: ${props => props.theme.bgColor};
  border: 1px solid ${props => props.theme.borderGrayColor};
  border-radius: 8px;
  padding: 1.2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  height: fit-content;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #22468A, #4285f4);
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
    border-color: #e1e5e9;

    &::before {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 6px;
    min-height: 120px;
  }
`;

const RichSourceHeader = styled.div`
  margin-bottom: 0.8rem;
  flex-shrink: 0;
`;

const RichSourceTitle = styled.a`
  display: block;
  font-size: 1.4rem;
  font-weight: 600;
  color: ${props => props.theme.textDefaultColor === '#FFF' ? '#FFF' : '#22468A'};
  text-decoration: none;
  line-height: 1.3;
  margin-bottom: 0.5rem;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;

  &:hover {
    color: ${props => props.theme.textDefaultColor === '#FFF' ? '#3B82F6' : '#1a3570'};
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 0.4rem;
    -webkit-line-clamp: 3;
  }
`;

const RichSourceDate = styled.div`
  font-size: 1.1rem;
  color: ${props => props.theme.textThinColor};
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '';
    width: 4px;
    height: 4px;
    background-color: ${props => props.theme.textThinColor};
    border-radius: 50%;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const RichSourceSnippet = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.textDefaultColor === '#FFF' ? '#FFF' : '#666'};
  line-height: 1.4;
  margin: 0;
  text-align: left;
  flex-grow: 1;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.3;
    -webkit-line-clamp: 4;
  }
`;

const BookAuthor = styled.div`
  font-size: 1.2rem;
  color: ${props => props.theme.textDefaultColor === '#FFF' ? '#D1D5DB' : '#666'};
  font-weight: 400;
  margin-top: 0.3rem;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

// 파일 정보 스타일 컴포넌트들
const FileInfoContainer = styled.div`
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    margin-top: 1.2rem;
  }
`;

const FileInfoTitle = styled.h4`
  font-size: 1.6rem;
  font-weight: 600;
  color: ${props => props.theme.textDefaultColor === '#FFF' ? '#FFF' : '#22468A'};
  margin-bottom: 1rem;
  margin-top: 0;

  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 0.8rem;
  }
`;

const FileInfoGrid = styled.div<{ $hasMultiple: boolean }>`
  display: ${props => props.$hasMultiple ? 'grid' : 'block'};
  grid-template-columns: ${props => props.$hasMultiple ? 'repeat(auto-fill, minmax(280px, 1fr))' : 'none'};
  gap: ${props => props.$hasMultiple ? '1rem' : '0'};

  @media (max-width: 768px) {
    grid-template-columns: ${props => props.$hasMultiple ? '1fr' : 'none'};
    gap: ${props => props.$hasMultiple ? '0.8rem' : '0'};
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: ${props => props.$hasMultiple ? 'repeat(2, 1fr)' : 'none'};
  }

  @media (min-width: 1025px) {
    grid-template-columns: ${props => props.$hasMultiple ? 'repeat(3, 1fr)' : 'none'};
  }
`;

const FileInfoCard = styled.div<{ $isImage?: boolean }>`
  background: ${props => props.theme.bgColor};
  border: 1px solid ${props => props.theme.borderGrayColor};
  border-radius: 8px;
  padding: ${props => props.$isImage ? '1rem' : '1.2rem'};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${props => props.$isImage ? '#10B981, #34D399' : '#22468A, #4285f4'});
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
    border-color: #e1e5e9;

    &::before {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    padding: ${props => props.$isImage ? '0.8rem' : '1rem'};
    border-radius: 6px;
  }
`;

const FileInfoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.8rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const FileInfoName = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${props => props.theme.textDefaultColor === '#FFF' ? '#FFF' : '#22468A'};
  line-height: 1.3;
  flex: 1;
  min-width: 0;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const FileExtension = styled.span<{ $isImage?: boolean }>`
  background: linear-gradient(135deg, ${props => props.$isImage ? '#10B981, #34D399' : '#22468A, #4285f4'});
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  position: relative;

  ${props => props.$isImage && `
    &::before {
      content: '🖼️';
      margin-right: 0.3rem;
    }
  `}

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.25rem 0.6rem;
  }
`;

const FileDownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg,rgb(102, 126, 234),rgb(118, 75, 162));
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.3rem;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;

  &:hover {
    background: linear-gradient(135deg,rgb(102, 126, 234),rgb(118, 75, 162));
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    color: white;
    text-decoration: none;
  }

  &:active {
    transform: translateY(0);
  }

  &::before {
    content: '📎';
    font-size: 1.2rem;
  }

  @media (max-width: 768px) {
    padding: 0.7rem 1.2rem;
    font-size: 1.2rem;

    &::before {
      font-size: 1.1rem;
    }
  }
`;

// 이미지 파일 관련 스타일 컴포넌트들
const FileImageContainer = styled.div`
  position: relative;
  margin-top: 0.8rem;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f9fa;

  &:hover {
    .file-image-overlay {
      opacity: 1;
    }
  }
`;

const FileImage = styled.img`
  width: 100%;
  height: auto;
  max-height: 300px;
  object-fit: contain;
  display: block;
  border-radius: 4px;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    max-height: 250px;
  }
`;

const FileImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, transparent 0%, transparent 70%, rgba(0, 0, 0, 0.8) 100%);
  opacity: 0;
  transition: opacity 0.2s ease;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 1rem;

  &.file-image-overlay {
    /* 클래스 추가로 hover 타겟팅 */
  }

  a {
    background: rgba(255, 255, 255, 0.95);
    color: #22468A;
    padding: 0.6rem 1.2rem;
    border-radius: 6px;
    text-decoration: none;
    font-weight: 600;
    font-size: 1.2rem;
    backdrop-filter: blur(10px);
    transition: all 0.2s ease;

    &:hover {
      background: white;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      color: #22468A;
      text-decoration: none;
    }

    &::before {
      content: '⬇️';
      margin-right: 0.5rem;
    }
  }

  @media (max-width: 768px) {
    padding: 0.8rem;

    a {
      padding: 0.5rem 1rem;
      font-size: 1.1rem;
    }
  }
`;
